import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Field } from '../../components/kit';
import { ResetPasswordModal } from '../../components/ResetPasswordModal';
import { PageHead, Toolbar, Search, ListTable, Pager, CustomerCell, ActBtn } from '../../components/list';
import { adminApi, type Dealer } from '../../api/admin';
import { ApiRequestError } from '../../api/client';
import { useAuth } from '../../auth/AuthContext';
import '../page.css';
import './admin.css';

const EMPTY = { fullName: '', companyName: '', email: '', phone: '', taxNumber: '', address: '', password: '' };
type FormState = typeof EMPTY;

export function Dealers() {
  const [rows, setRows] = useState<Dealer[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [mode, setMode] = useState<null | 'create' | number>(null);
  const [form, setForm] = useState<FormState>({ ...EMPTY });
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [resetFor, setResetFor] = useState<Dealer | null>(null);
  const [impersonating, setImpersonating] = useState<number | null>(null);
  const { user, impersonate } = useAuth();
  const isAdmin = user?.role === 'admin';
  const navigate = useNavigate();

  const loginAsDealer = async (d: Dealer) => {
    setImpersonating(d.id);
    try {
      await impersonate(d.id);
      navigate('/bayi');
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Bayi girişi başarısız');
      setImpersonating(null);
    }
  };

  const load = () => { adminApi.dealers(q, page).then((r) => { setRows(r.dealers); setTotal(r.total); }); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [page]);

  const openCreate = () => { setForm({ ...EMPTY }); setError(null); setMode('create'); };
  const openEdit = (d: Dealer) => {
    setForm({
      fullName: d.full_name ?? '', companyName: d.company_name ?? '', email: d.email,
      phone: d.phone ?? '', taxNumber: d.tax_number ?? '', address: d.address ?? '', password: '',
    });
    setError(null);
    setMode(d.id);
  };

  const toggleActive = async (d: Dealer) => { await adminApi.updateDealer(d.id, { isActive: !d.is_active }); load(); };

  const remove = async (d: Dealer) => {
    if (!window.confirm(`"${d.company_name || d.full_name || d.email}" bayisi ve ona bağlı tüm kayıtları (ödemeler dahil) kalıcı olarak silinecek. Emin misiniz?`)) return;
    setError(null);
    try { await adminApi.deleteDealer(d.id); load(); }
    catch (err) { setError(err instanceof ApiRequestError ? err.message : 'Silinemedi'); }
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      if (mode === 'create') {
        await adminApi.createDealer(form);
      } else if (typeof mode === 'number') {
        const r = await adminApi.updateDealer(mode, {
          fullName: form.fullName, companyName: form.companyName, email: form.email,
          taxNumber: form.taxNumber, phone: form.phone, address: form.address,
        });
        if (r.emailPending) {
          setNotice(`Yeni e-posta adresine (${form.email}) onay bağlantısı gönderildi. Bayi onaylayana kadar giriş yapamaz.`);
        }
      }
      setMode(null);
      setForm({ ...EMPTY });
      load();
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'İşlem başarısız');
    } finally {
      setBusy(false);
    }
  };

  const set = (k: keyof FormState) => (e: ChangeEvent<HTMLInputElement>) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const pages = Math.max(1, Math.ceil(total / 20));
  const isEdit = typeof mode === 'number';

  return (
    <div className="page">
      <PageHead
        eyebrow="Yönetim" title="Bayi Yönetimi" sub="Bayileri arayın, düzenleyin ve hesaplarına giriş yapın."
        right={
          <button className="lt-btn primary" onClick={mode === 'create' ? () => setMode(null) : openCreate}>
            {mode === 'create' ? 'Vazgeç' : '+ Yeni Bayi'}
          </button>
        }
      />

      {notice && (
        <div style={{ background: 'var(--good-wash)', color: 'var(--good)', border: '1px solid color-mix(in srgb, var(--good) 25%, transparent)', borderRadius: 12, padding: '12px 14px', marginBottom: 14, fontSize: 13.5, fontWeight: 500, display: 'flex', justifyContent: 'space-between', gap: 12 }}>
          <span>✉️ {notice}</span>
          <button className="linklike" onClick={() => setNotice(null)}>Kapat</button>
        </div>
      )}

      {mode !== null && (
        <Card title={isEdit ? 'Bayi Düzenle' : 'Yeni Bayi'} className="bank-card">
          <form onSubmit={submit}>
            {error && <div className="auth-error" style={{ marginBottom: 12 }}>{error}</div>}
            <div className="inline-form">
              <Field label="Ad Soyad" id="fn" value={form.fullName} onChange={set('fullName')} required />
              <Field label="Firma Adı" id="cn" value={form.companyName} onChange={set('companyName')} required />
              <Field label="E-posta" id="em" type="email" value={form.email} onChange={set('email')} required />
              <Field label="Telefon" id="ph" value={form.phone} onChange={set('phone')} />
              <Field label="Vergi No" id="tx" value={form.taxNumber} onChange={set('taxNumber')} />
              {!isEdit && <Field label="Geçici Şifre (min 8)" id="pw" type="text" value={form.password} onChange={set('password')} required />}
              <div className="full"><Field label="Adres" id="ad" value={form.address} onChange={set('address')} /></div>
            </div>
            <div className="actions">
              <Button type="submit" disabled={busy}>{busy ? 'Kaydediliyor…' : isEdit ? 'Kaydet' : 'Bayi Oluştur'}</Button>
              <Button type="button" variant="secondary" onClick={() => setMode(null)}>Vazgeç</Button>
            </div>
          </form>
        </Card>
      )}

      <Toolbar>
        <Search value={q} onChange={setQ} onEnter={() => { setPage(1); load(); }} placeholder="Ad / firma / e-posta ara…" />
        <span className="lt-grow" />
        <span className="lt-count">{total} bayi</span>
      </Toolbar>

      <ListTable
        storageKey="admin-dealers"
        head={[
          { label: 'Firma / Ad', width: 300 },
          { label: 'E-posta', width: 300 },
          { label: 'Telefon', width: 160 },
          { label: 'Durum', width: 120 },
          { label: 'İşlem', width: 210, right: true },
        ]}
        empty={rows.length === 0 ? <div className="lt-empty">Bayi bulunamadı.</div> : undefined}
        pager={<Pager page={page} pages={pages} total={total} unit="bayi" onPage={setPage} />}
      >
        {rows.map((d) => (
          <tr key={d.id}>
            <td><CustomerCell primary={d.company_name || d.full_name || '—'} secondary={d.company_name ? d.full_name ?? undefined : undefined} /></td>
            <td title={d.email}>{d.email}</td>
            <td>{d.phone || '—'}</td>
            <td><span className={`lt-pill ${d.is_active ? 'ok' : 'fail'}`}>{d.is_active ? 'Aktif' : 'Pasif'}</span></td>
            <td>
              <div className="lt-acts">
                <ActBtn icon="edit" title="Düzenle" onClick={() => openEdit(d)} />
                <ActBtn icon="key" title="Şifre Sıfırla" onClick={() => setResetFor(d)} />
                <ActBtn icon="power" title={d.is_active ? 'Pasifleştir' : 'Aktifleştir'} onClick={() => toggleActive(d)} />
                {d.is_active && (
                  <ActBtn icon="login" tone="accent" title="Bayi hesabına giriş yap"
                    onClick={() => loginAsDealer(d)} disabled={impersonating === d.id} />
                )}
                {isAdmin && <ActBtn icon="trash" tone="danger" title="Bayiyi Sil" onClick={() => remove(d)} />}
              </div>
            </td>
          </tr>
        ))}
      </ListTable>

      <ResetPasswordModal
        target={resetFor ? { id: resetFor.id, label: resetFor.company_name || resetFor.full_name || resetFor.email } : null}
        resetFn={adminApi.resetPassword}
        onClose={() => setResetFor(null)}
      />
    </div>
  );
}
